import { useState, useEffect } from "react";
import Button from "../../../components/ui-custom/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui-custom/Dialog";
import { Input } from "../../../components/ui-custom/Input";
import { Trash2, AlertCircle } from "lucide-react";

// Inline components since they are missing in ui-custom
const Badge = ({ variant = "none", className = "", children }) => {
    const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
        none: "",
        default: "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
        secondary: "border-transparent bg-sm-secondary text-sm-secondary-foreground hover:bg-sm-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
    };
    return (
        <div className={`${baseStyles} ${variants[variant]} ${className}`}>
            {children}
        </div>
    );
};

const Label = ({ className = "", children, ...props }) => (
    <label className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`} {...props}>
        {children}
    </label>
);


export function EditMilestonesModal({ open, onOpenChange, milestones, totalOrderValue, onSave }) {
    const [localMilestones, setLocalMilestones] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            // Initialize with milestones, ensuring unpaid ones are editable
            // Paid milestones should be locked? User prompt says "User will first adjust...".
            // Assuming paid ones are locked for editing amount but maybe name? 
            // Actually usually paid ones are locked.
            setLocalMilestones(milestones.map(m => ({
                ...m,
                amount: m.value || 0, // ensure we work with 'amount'
                isPaid: m.status === "Paid"
            })));
            setError(null);
        }
    }, [open, milestones]);

    const handleUpdate = (id, field, value) => {
        setLocalMilestones(prev => prev.map(m => {
            if (m.id === id) {
                return { ...m, [field]: value };
            }
            return m;
        }));
    };

    const currentTotal = localMilestones.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
    const diff = totalOrderValue - currentTotal;
    const isValid = Math.abs(diff) < 0.01; // minimal floating point tolerance

    const handleSave = () => {
        if (!isValid) {
            setError(`Total milestone value must equal the total work order value. Difference: ₹${diff.toFixed(2)}`);
            return;
        }
        onSave(localMilestones);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange} className="max-w-[60vw]">
            <DialogContent className="w-full max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Edit Payment Milestones</DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto py-4">
                    <div className="space-y-4">
                        <div className="bg-muted/30 p-4 rounded-lg flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Total Work Order Value</p>
                                <p className="text-lg font-bold">₹{totalOrderValue.toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm font-medium text-muted-foreground">Current Allocation</p>
                                <p className={`text-lg font-bold ${isValid ? 'text-green-600' : 'text-red-500'}`}>
                                    ₹{currentTotal.toLocaleString()}
                                </p>
                                {!isValid && (
                                    <p className="text-xs text-red-500 font-medium">
                                        {diff > 0 ? `Remaining: ₹${diff.toLocaleString()}` : `Overallocated: ₹${Math.abs(diff).toLocaleString()}`}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="border rounded-md">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium">Milestone Name</th>
                                        <th className="px-4 py-2 text-left font-medium">Description</th>
                                        <th className="px-4 py-2 text-right font-medium">Value</th>
                                        <th className="px-4 py-2 text-center font-medium">Status</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {localMilestones.map((milestone) => (
                                        <tr key={milestone.id} className="border-t">
                                            <td className="p-3">
                                                <Input
                                                    value={milestone.name}
                                                    onChange={(e) => handleUpdate(milestone.id, 'name', e.target.value)}
                                                    disabled={milestone.isPaid}
                                                    className="h-8"
                                                />
                                            </td>
                                            <td className="p-3">
                                                <Input
                                                    value={milestone.description}
                                                    onChange={(e) => handleUpdate(milestone.id, 'description', e.target.value)}
                                                    disabled={milestone.isPaid}
                                                    className="h-8"
                                                />
                                            </td>
                                            <td className="p-3">
                                                <Input
                                                    type="number"
                                                    value={milestone.amount}
                                                    onChange={(e) => handleUpdate(milestone.id, 'amount', e.target.value)}
                                                    disabled={milestone.isPaid}
                                                    className="h-8 text-right"
                                                />
                                            </td>
                                            <td className="p-3 text-center">
                                                <Badge variant={milestone.status === "Paid" ? "default" : "outline"}>
                                                    {milestone.status}
                                                </Badge>
                                            </td>
                                            <td className="p-3 text-center">
                                                {milestone.isPaid && <span className="text-xs text-muted-foreground">Locked</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex items-center justify-between sm:justify-between w-full">
                    <div className="flex-1">
                        {error && (
                            <div className="flex items-center text-destructive text-sm gap-2">
                                <AlertCircle className="h-4 w-4" />
                                {error}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={!isValid}>Save Changes</Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
